import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  inject,
  type OnDestroy,
  type OnInit,
  signal,
  viewChild,
  effect,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  LucideHistory,
  LucideLoaderCircle,
  LucidePlus,
  LucideX,
  LucideTrash2,
} from '@lucide/angular';
import { TranslatePipe } from '@ngx-translate/core';
import {
  FormInputSearchComponent,
  H3Component,
  PmButtonComponent,
  PmIconComponent,
} from '@libs/ui';
import { DateUtilsService } from '@core/utils/date-utils.service';
import { ChatStateService } from '../../services/chat-state.service';
import { ChatHistoryService } from '../../services/chat-history.service';

@Component({
  selector: 'pm-chat-history',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './chat-history.html',
  host: {
    class: 'absolute inset-0 z-[5] block',
    '(document:keydown.escape)': 'chat.showChat()',
  },
  imports: [
    TranslatePipe,
    FormsModule,
    FormInputSearchComponent,
    H3Component,
    PmButtonComponent,
    PmIconComponent,
  ],
})
export class ChatHistoryComponent implements OnInit, OnDestroy {
  protected readonly dateUtils = inject(DateUtilsService);
  protected readonly chat = inject(ChatStateService);
  protected readonly chatHistory = inject(ChatHistoryService);

  protected readonly LucideX = LucideX;
  protected readonly LucidePlus = LucidePlus;
  protected readonly LucideHistory = LucideHistory;
  protected readonly LucideTrash2 = LucideTrash2;
  protected readonly LucideLoaderCircle = LucideLoaderCircle;

  protected readonly searchValue = signal('');

  private readonly closeButton = viewChild('closeButton', {
    read: ElementRef,
  });
  readonly hasAnySession = computed(
    () => this.chatHistory.sessions().length > 0,
  );
  readonly isLoadingFirstPage = computed(
    () =>
      this.chatHistory.isLoading() && this.chatHistory.sessions().length === 0,
  );
  readonly isLoadingMore = computed(
    () =>
      this.chatHistory.isLoading() && this.chatHistory.sessions().length > 0,
  );
  private readonly sentinel = viewChild<ElementRef<HTMLElement>>('sentinel');

  private observer?: IntersectionObserver;

  constructor() {
    afterNextRender(() =>
      this.closeButton()?.nativeElement.querySelector('button')?.focus(),
    );

    effect(() => {
      const el = this.sentinel()?.nativeElement;
      this.observer?.disconnect();
      if (!el) {
        return;
      }
      this.observer = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting) {
          this.chatHistory.loadMore();
        }
      });
      this.observer.observe(el);
    });
  }

  ngOnInit(): void {
    this.chatHistory.loadFirstPage();
  }

  ngOnDestroy(): void {
    this.observer?.disconnect();
  }

  protected startNewChat(): void {
    this.chat.startNewChat();
    this.chat.showChat();
  }

  protected onSearch(value: string): void {
    this.searchValue.set(value);
    this.chatHistory.search(value);
  }

  protected onLoadSession(sessionId: string): void {
    this.chat.loadSession(sessionId);
  }

  protected onDelete(sessionId: string, event: Event): void {
    event.stopPropagation();
    this.chat.deleteSession(sessionId);
  }
}
